/**
 * 邮件撰写面板组件 — 左右分栏布局，左侧为邮件撰写区，右侧为客户信息匹配区。
 *
 * 主要导出：`CrmEmailComposePanel`（自定义元素 `<crm-email-compose-panel>`）
 *
 * 功能特性：
 * - 左侧 70% 宽度放置 `<crm-email-compose>` 邮件撰写组件
 * - 右侧 30% 宽度放置 `<crm-customer-info-panel>` 客户信息面板
 * - 监听撰写组件的 `recipients-changed` 事件，自动按收件人邮箱匹配客户
 * - 支持草稿恢复、转发、回复等多种邮件操作模式
 * - 收件人变化时使用 300ms 防抖延迟避免频繁请求
 *
 * 依赖关系：
 * - 继承 `BaseElement`
 * - 通过 `customerApi.matchByEmails` 批量匹配客户
 * - 内部使用 `<crm-email-compose>` 和 `<crm-customer-info-panel>` 子组件
 *
 * 对外事件：
 * - `crm-create-customer` — 用户在右侧面板点击「创建客户」时冒泡
 */
import { html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { BaseElement } from "../base-element.js";
import { customerApi } from "../api.js";
import type { CustomerMatch } from "./customer-info-panel.js";
import "./email-compose.js";
import "./customer-info-panel.js";

@customElement("crm-email-compose-panel")
export class CrmEmailComposePanel extends BaseElement {
  static styles = css`
    :host {
      display: block;
      flex: 1;
      min-height: 0;
    }
  `;
  /** 收件人邮箱（直接填写模式，非草稿恢复时使用） */
  @property({ type: String }) toEmail = "";
  /** 邮件主题 */
  @property({ type: String }) subject = "";
  /** 关联的客户 ID */
  @property({ type: Number }) customerId = 0;
  /** 草稿 ID（非 0 表示恢复草稿模式） */
  @property({ type: Number }) draftId = 0;
  /** 草稿正文内容 */
  @property({ type: String }) draftContent = "";
  /** 草稿收件人列表 */
  @property({ type: Array }) draftTo: string[] = [];
  /** 草稿抄送列表 */
  @property({ type: Array }) draftCc: string[] = [];
  /** 草稿关联的邮件账户 ID */
  @property({ type: Number }) draftAccountId = 0;
  /** 草稿附件列表（id / filename / size） */
  @property({ type: Array }) draftAttachments: Array<{ id: number; filename: string; size: number }> = [];
  /** 撰写会话标识，递增后触发面板重置（清除匹配结果和计时器） */
  @property({ type: Number }) composeSession = 0;
  /** 转发邮件的头部 HTML（用于在正文前插入转发信息） */
  @property({ type: String }) forwardHeader = "";
  /** 转发邮件的原始正文 HTML */
  @property({ type: String }) forwardHtml = "";
  /** 回复邮件的 Message-ID（用于设置 In-Reply-To 头） */
  @property({ type: String }) replyToMessageId = "";
  /** 回复邮件的 References 头（邮件线程引用链） */
  @property({ type: String }) replyReferences = "";
  /** 回复邮件的线程 ID */
  @property({ type: String }) replyThreadId = "";

  /** 已匹配到客户的收件人列表（同一客户可能有多个邮箱） */
  @state() matchedCustomers: CustomerMatch[] = [];
  /** 未匹配到客户的邮箱列表（提示用户创建新客户） */
  @state() unmatchedEmails: string[] = [];
  /** 客户匹配加载中状态 */
  @state() loading = false;

  /** 收件人变化防抖计时器句柄 */
  private _recipientTimer: number | null = null;

  /**
   * 收件人变化事件处理 — 防抖 300ms 后批量匹配客户。
   * 将 to + cc 合并去重后调用 `customerApi.matchByEmails`，
   * 结果按客户 ID 去重（同一客户多个邮箱归为一组）。
   * @param e - `recipients-changed` 自定义事件，detail 包含 to 和 cc 邮箱数组
   * @sideeffect 更新 matchedCustomers、unmatchedEmails、loading 状态
   */
  private _onRecipientsChanged(e: CustomEvent) {
    const { to, cc } = e.detail as { to: string[]; cc: string[] };
    // 合并 to 和 cc 并去重，过滤掉不含 @ 的无效地址
    const allEmails = [...new Set([...to, ...cc])].filter((e) =>
      e.includes("@"),
    );
    if (allEmails.length === 0) {
      // 无收件人时清空匹配结果
      this.matchedCustomers = [];
      this.unmatchedEmails = [];
      this.loading = false;
      return;
    }
    this.loading = true;
    // 防抖：300ms 内多次变化只发一次请求
    if (this._recipientTimer) clearTimeout(this._recipientTimer);
    this._recipientTimer = window.setTimeout(async () => {
      this._recipientTimer = null;
      try {
        const results = await customerApi.matchByEmails(allEmails);
        // 按客户 ID 去重：同一客户的多个邮箱归到同一组
        const customerMap = new Map<number, CustomerMatch>();
        const matchedEmails = new Set<string>();
        for (const r of results) {
          matchedEmails.add(r.email);
          const existing = customerMap.get(r.customer.id);
          if (existing) {
            existing.emails.push(r.email);
          } else {
            customerMap.set(r.customer.id, {
              customer: r.customer,
              emails: [r.email],
            });
          }
        }
        this.matchedCustomers = Array.from(customerMap.values());
        // 未匹配到客户的邮箱
        this.unmatchedEmails = allEmails.filter((e) => !matchedEmails.has(e));
      } catch {
        this.matchedCustomers = [];
        this.unmatchedEmails = [];
      }
      this.loading = false;
    }, 300);
  }

  /**
   * 创建客户事件处理 — 将子组件的 `crm-create-customer` 事件转发给父组件。
   * @param e - 事件 detail 包含待创建客户的邮箱
   */
  private _onCreateCustomer(e: CustomEvent) {
    const { email } = e.detail as { email: string };
    this.dispatchEvent(
      new CustomEvent("crm-create-customer", {
        detail: { email },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * 插入模板事件处理 — 将模板内容插入到富文本编辑器光标位置。
   * 通过 shadow DOM 查找链：compose → rich-editor → insertAtCursor。
   * @param e - 事件 detail 包含模板 HTML content
   */
  private _onInsertTemplate(e: CustomEvent) {
    const { content } = e.detail as { content: string };
    const compose = this.shadowRoot?.querySelector('crm-email-compose') as any;
    if (!compose) return;
    const editor = compose.shadowRoot?.querySelector('crm-rich-editor') as any;
    if (!editor) return;
    editor.insertAtCursor(content);
  }

  /**
   * 刷新客户匹配 — 手动触发子组件的收件人变化检测。
   * 在外部创建客户后调用，使匹配结果更新。
   */
  refreshCustomerMatch() {
    const compose = this.shadowRoot?.querySelector("crm-email-compose") as any;
    if (compose) {
      compose._scheduleRecipientsChanged?.();
    }
  }

  /**
   * Tab 切换事件处理 — 预留扩展点，当前仅允许事件冒泡。
   * @param _e - Tab 切换事件（下划线前缀表示当前未使用参数）
   */
  private _onTabChange(_e: CustomEvent) {
    // Future tabs handled here; for now just allows event to bubble
  }

  /**
   * 生命周期：属性变化后触发。
   * 当 composeSession 递增时（新建邮件/切换会话），重置匹配状态和计时器。
   * @param changedProps - 发生变化的属性 Map
   */
  updated(changedProps: Map<string, any>) {
    if (changedProps.has('composeSession') && this.composeSession > 0) {
      this.matchedCustomers = [];
      this.unmatchedEmails = [];
      this.loading = false;
      if (this._recipientTimer) clearTimeout(this._recipientTimer);
    }
  }

  /** 生命周期：组件从 DOM 移除时清理防抖计时器，防止内存泄漏 */
  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._recipientTimer) clearTimeout(this._recipientTimer);
  }

  /**
   * 渲染左右分栏布局。
   * 左侧 flex-[7]（70%）：邮件撰写区；右侧 flex-[3]（30%）：客户信息面板。
   * @returns Lit 模板
   */
  render() {
    return html`
      <div class="flex" style="height:100%">
        <div class="flex-[7] overflow-hidden flex flex-col min-h-0 pr-3">
          <crm-email-compose
            .toEmail=${this.toEmail}
            .subject=${this.subject}
            .customerId=${this.customerId}
            .draftId=${this.draftId}
            .draftContent=${this.draftContent}
            .draftTo=${this.draftTo}
            .draftCc=${this.draftCc}
            .draftAccountId=${this.draftAccountId}
            .draftAttachments=${this.draftAttachments}
            .composeSession=${this.composeSession}
            .forwardHeader=${this.forwardHeader}
            .forwardHtml=${this.forwardHtml}
            .replyToMessageId=${this.replyToMessageId}
            .replyReferences=${this.replyReferences}
            .replyThreadId=${this.replyThreadId}
            @recipients-changed=${this._onRecipientsChanged}
          >
          </crm-email-compose>
        </div>
        <div class="flex-[3] pl-4 overflow-y-auto">
          <crm-customer-info-panel
            .matchedCustomers=${this.matchedCustomers}
            .unmatchedEmails=${this.unmatchedEmails}
            .loading=${this.loading}
            .composeSession=${this.composeSession}
            @crm-tab-change=${this._onTabChange}
            @crm-create-customer=${this._onCreateCustomer}
            @insert-template=${this._onInsertTemplate}
          >
          </crm-customer-info-panel>
        </div>
      </div>
    `;
  }
}
